import type { ReactNode } from 'react'
import { useTranslate, type TranslationKey } from 'shared/lib/i18n'

/**
 * A page under its own header — the hub's look (the redesign's reference): the
 * title, then what the page is for. The hub's and the central cashbox's pages are
 * each framed by it, titled by their sidebar label (`routes.tsx`); a page about one
 * thing (a Parcel, a Bag) frames itself, titled by that thing.
 *
 * `title` is a translation key; `heading` replaces it with the thing's own name (a
 * code and its status `Badge`). `back` sits before the title (a link to the list);
 * `actions` at the header's right: the page's one main button and its «⋯» menu.
 */
export function PageFrame({
	title,
	heading,
	back,
	actions,
	children,
}: {
	title?: TranslationKey
	heading?: ReactNode
	back?: ReactNode
	actions?: ReactNode
	children: ReactNode
}) {
	const { t } = useTranslate()
	return (
		<div className='mx-auto flex w-full max-w-6xl flex-col gap-6'>
			<div className='flex flex-wrap items-center gap-3'>
				{back}
				<h1 className='flex min-w-0 items-center gap-3 text-2xl font-semibold tracking-tight'>
					{heading ?? (title && t(title))}
				</h1>
				{actions && <div className='ml-auto flex shrink-0 items-center gap-2'>{actions}</div>}
			</div>
			{children}
		</div>
	)
}
